import { apiFetch } from './http';
import { retryAfterSeconds } from './retryAfter';

// ─────────────────────────────────────────────────────────────────────
// ASKING A CLUB TO LET YOU IN, AS A SIGNED-IN PERSON (#710).
//
// `POST /me/clubs/:slug/join-request`, which exists because this one did not:
// the only join-request door needed an `spo_` ONBOARDING GRANT, so a verified
// account had no way to ask — and the player home reached for the registration
// journey instead, asking somebody who had just proved who they were for their
// name and address again. Dogfooding found it (#790).
//
// ── DISCOVERY IS NOT APPLICATION ──────────────────────────────────
//
// `searchClubs` is the public directory: unauthenticated, nothing to do with
// identity, and the thing a person uses to LOOK. This is the separate,
// authenticated act of asking one for something. Keeping them apart is the
// point — nobody should have to apply in order to browse.
// ─────────────────────────────────────────────────────────────────────

/**
 * The outcome, as one flat shape.
 *
 * `code` is a MACHINE code from the API and never a sentence: the server
 * answers `already_member`, `too_many_pending`, `no_player`; the browser
 * decides what to say. `null` means the request now stands.
 */
export interface JoinClubOutcome {
  code: string | null;
  /** On `reapply_not_yet`: when they may ask again. Null when not given. */
  from: string | null;
  /** True when this call created the request; false when one already stood. */
  created: boolean;
  /**
   * On `too_many_attempts` only: the server's `Retry-After` in whole seconds,
   * 0 when it could not be read (#778 Q7).
   */
  retryAfterSeconds?: number;
}

export async function askToJoinClub(slug: string): Promise<JoinClubOutcome> {
  let res: Awaited<ReturnType<typeof apiFetch>>;
  try {
    res = await apiFetch(`/me/clubs/${encodeURIComponent(slug)}/join-request`, {
      method: 'POST',
    });
  } catch {
    return { code: 'unreachable', from: null, created: false };
  }
  const body = (await res.json().catch(() => null)) as {
    error?: unknown;
    from?: unknown;
    status?: unknown;
  } | null;

  if (!res.ok) {
    const code = typeof body?.error === 'string' ? body.error : 'unknown_error';
    const from = typeof body?.from === 'string' ? body.from : null;
    return code === 'too_many_attempts'
      ? { code, from, created: false, retryAfterSeconds: retryAfterSeconds(res) }
      : { code, from, created: false };
  }
  // 201 CREATED a request; 200 means one already stood and the server answered
  // with it. Both are success, and the difference is worth keeping: «we have
  // your request» reads oddly as a response to tapping a button twice.
  return { code: null, from: null, created: res.status === 201 };
}

/**
 * A club this person may not ask right now (#1302), in the SAME machine codes
 * the ask itself answers: `reapply_not_yet` (with `from`), `reapply_blocked`,
 * `request_closed`. The server decides it with the gate the ask runs, so the
 * button and the refusal cannot disagree.
 */
export interface ClosedJoinRequest {
  slug: string;
  code: 'reapply_not_yet' | 'reapply_blocked' | 'request_closed';
  /** On `reapply_not_yet`: the ISO instant they may ask again. Null otherwise. */
  from: string | null;
}

/** What `GET /me/join-requests` says about this person's standing with clubs. */
export interface MyJoinStanding {
  /** The slugs of the clubs asked that have not answered. */
  pending: string[];
  /** The clubs whose answer means not now, or not again (#1302). */
  closed: ClosedJoinRequest[];
}

const CLOSED_CODES: ReadonlySet<string> = new Set([
  'reapply_not_yet',
  'reapply_blocked',
  'request_closed',
]);

/**
 * The clubs this person has asked and that have not answered — and those
 * whose answer means they may not ask yet — `GET /me/join-requests`.
 *
 * What lets the directory say «Venter på godkjenning», or «Du kan søke igjen
 * fra 2. des.», instead of offering a button the server will refuse. A
 * FAILURE ANSWERS EMPTY, on purpose: the worst it costs is the ordinary
 * button, and tapping it is safe — the server answers a standing application
 * with that application, and a refusal with its reason and date.
 *
 * A `reapply_not_yet` entry without a readable date is DROPPED rather than
 * shown as «senere»: the date is the whole point (#1302), and the ask still
 * answers with it.
 */
export async function readMyJoinRequests(signal?: AbortSignal): Promise<MyJoinStanding> {
  const empty: MyJoinStanding = { pending: [], closed: [] };
  try {
    const res = await apiFetch('/me/join-requests', { signal });
    if (!res.ok) return empty;
    const body = (await res.json().catch(() => null)) as {
      requests?: unknown;
      closed?: unknown;
    } | null;
    const pending = Array.isArray(body?.requests)
      ? body.requests
          .map((r) => (r && typeof r === 'object' ? (r as Record<string, unknown>).slug : null))
          .filter((slug): slug is string => typeof slug === 'string' && slug !== '')
      : [];
    const closed: ClosedJoinRequest[] = [];
    if (Array.isArray(body?.closed)) {
      for (const raw of body.closed) {
        if (!raw || typeof raw !== 'object') continue;
        const r = raw as Record<string, unknown>;
        if (typeof r.slug !== 'string' || r.slug === '') continue;
        if (typeof r.code !== 'string' || !CLOSED_CODES.has(r.code)) continue;
        const code = r.code as ClosedJoinRequest['code'];
        const from =
          typeof r.from === 'string' && Number.isFinite(Date.parse(r.from)) ? r.from : null;
        if (code === 'reapply_not_yet' && from === null) continue;
        closed.push({ slug: r.slug, code, from: code === 'reapply_not_yet' ? from : null });
      }
    }
    return { pending, closed };
  } catch {
    return empty;
  }
}

/**
 * Take a pending application back — `DELETE /me/clubs/:slug/join-request`
 * (#729, owner decision 2026-11-02). The person may ask again at once.
 *
 * `code` is the server's MACHINE code, never a sentence: `not_pending` (the
 * club has answered), `no_request`, `unknown_club`. `null` means it is
 * withdrawn.
 */
export async function withdrawJoinRequest(slug: string): Promise<{ code: string | null }> {
  let res: Awaited<ReturnType<typeof apiFetch>>;
  try {
    res = await apiFetch(`/me/clubs/${encodeURIComponent(slug)}/join-request`, {
      method: 'DELETE',
    });
  } catch {
    return { code: 'unreachable' };
  }
  if (res.ok) return { code: null };
  const body = (await res.json().catch(() => null)) as { error?: unknown } | null;
  return { code: typeof body?.error === 'string' ? body.error : 'unknown_error' };
}
